import styled, { css } from 'styled-components'
import media from 'styled-media-query'
import { fade } from 'styles/animations'

export const Wrapper = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.neutral.primary};
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
    padding: 10px ${theme.spacings.small};
    position: fixed;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-between;
    z-index: ${theme.layers.overlay};

    ${fade(1)}

    ${media.greaterThan('medium')`
      display: none;
    `}
  `}
`

const menuModifiers = {
  open: () => css`
    transform: translateY(0);
    opacity: 1;
    pointer-events: all;
  `,
}
export const Menu = styled.div`
  ${({ theme, isOpen }) => css`
    background-color: #efefef;
    border-radius: 16px 16px 0px 0px;
    padding: ${theme.spacings.medium};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: calc(100% - ${theme.spacings.xxsmall});
    width: 100vw;
    transform: translateY(100%);
    transition: all 0.5s ease;
    opacity: 0;
    pointer-events: none;
    z-index: ${theme.layers.alwaysOnTop};

    ${isOpen && menuModifiers.open()};
  `}
`

export const LogoWrapper = styled.a`
  svg,
  img {
    height: 34px;
    width: auto;
  }
`

export const Space = styled.span`
  flex-basis: 20%;
`

export const MenuNav = styled.nav`
  ${({ isOpen }) => css`
    align-self: flex-end;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-top: 20vh;

    a {
      transform: ${isOpen ? 'scaleX(1)' : 'scaleX(0)'};
      transition: transform 0.7s cubic-bezier(0.5, 0, 0, 1);
      transform-origin: 100%;

      &:first-child {
        transition-delay: 0.2s;
      }

      &:nth-child(2) {
        transition-delay: 0.3s;
      }

      &:nth-child(3) {
        transition-delay: 0.4s;
      }
    }

    div > a {
      transform: ${isOpen ? 'scaleX(1)' : 'scaleX(0)'};
      transition: transform 0.7s cubic-bezier(0.5, 0, 0, 1);
      transform-origin: 100%;
      outline: none;

      &:first-child {
        transition-delay: 0.5s;
      }
    }
  `}
`

const languageWrapperModifiers = {
  show: (theme) => css`
    font-weight: ${theme.font.black};
  `,
}

export const LanguageWrapper = styled.div`
  ${({ theme, show = true }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    padding-right: 12px;
    margin-right: -12px;

    a {
      margin-bottom: ${theme.spacings.xxsmall};
    }

    &::after {
      content: '';
      position: absolute;
      width: 1px;
      height: 112px;
      right: 20px;
      background-color: ${theme.colors.primary.main};
      transform-origin: 50% 0;
      transform: ${show ? 'scaleY(1)' : 'scaleY(0)'};
      transition: transform 1s cubic-bezier(0.5, 0, 0, 1);
    }

    ${show && languageWrapperModifiers.show(theme)};
  `}
`

const menuLinkModifiers = {
  active: (theme) => css`
    color: ${theme.colors.primary.main};
    opacity: 1;
  `,
}

export const MenuLink = styled.a`
  ${({ theme, active }) => css`
    display: flex;
    flex-wrap: wrap;
    color: ${theme.colors.grayscale.light.paragraph};
    font-size: 18px;
    font-family: ${theme.font.familyTertiary};
    font-weight: ${theme.font.black};
    line-height: 24px;
    align-items: center;
    margin-bottom: ${theme.spacings.small};
    opacity: 0.8;

    ${active && menuLinkModifiers.active(theme)};
  `}
`

const languageOptionModifiers = {
  active: (theme) => css`
    color: ${theme.colors.primary.main};

    &::after {
      background-color: ${theme.colors.primary.main};
      content: '';
      height: 100%;
      width: 3px;
      position: absolute;
      right: calc(-${theme.spacings.xxsmall} - 4px);
      top: -1px;
    }
  `,
}

export const LanguageOption = styled.button`
  ${({ theme, show, active = true }) => css`
    color: ${theme.colors.grayscale.light.paragraph};
    position: relative;
    font-size: 18px;
    font-family: ${theme.font.familyTertiary};
    font-weight: ${theme.font.black};
    line-height: 24px;
    padding: ${theme.spacings.xxsmall} 0;
    opacity: ${show ? '1' : '0'};
    pointer-events: ${show ? 'all' : 'none'};
    letter-spacing: ${show ? '0' : '3px'};
    transition: opacity 100ms ease-in-out 0s,
      letter-spacing 0.4s cubic-bezier(0.5, 0, 0, 1);

    &:nth-child(1) {
      transition-delay: 0.1s;
    }

    &:nth-child(2) {
      transition-delay: 0.2s;
    }

    ${active && languageOptionModifiers.active(theme)};
  `}
`

export const CloseButton = styled.button`
  ${({ theme, isOpen }) => css`
    position: absolute;
    right: 20px;
    top: 40px;
    width: 14.14px;
    height: 14.21px;
    z-index: 1;
    white-space: nowrap;

    span {
      background-color: ${theme.colors.primary.main};
      position: absolute;
      top: 0;
      width: 20px;
      height: 2px;
      transition: transform 0.7s cubic-bezier(0.5, 0, 0, 1),
        color 0.7s cubic-bezier(0.5, 0, 0, 1);

      &:first-child {
        left: 0;
        transform-origin: 0 50%;
        transform: ${isOpen
          ? 'rotate(45deg) scaleX(1)'
          : 'rotate(45deg) scaleX(0)'};
        transition-delay: 0.3s;
      }

      &:nth-child(2) {
        right: 0;
        transform-origin: 100% 50%;
        transform: ${isOpen
          ? 'rotate(-45deg) scaleX(1)'
          : 'rotate(-45deg) scaleX(0)'};
        transition-delay: 0.4s;
      }
    }
  `}
`

export const OpenMenu = styled.button`
  ${({ theme }) => css`
    color: ${theme.colors.primary};
  `}
`

export const OpenMenu2 = styled.a`
  ${({ theme, isOpen = false }) => css`
    position: absolute;
    right: 24px;
    top: 24px;
    width: 20px;
    height: 12px;
    z-index: 1;
    white-space: nowrap;

    span {
      background-color: ${theme.colors.primary.main};
      position: absolute;
      top: 0;
      left: 0;
      width: 16px;
      height: 1.96px;
      transition: transform 0.7s cubic-bezier(0.5, 0, 0, 1),
        color 0.7s cubic-bezier(0.5, 0, 0, 1);

      &:first-child {
        margin-top: 0;
        transform-origin: 100% 0;
        transform: ${isOpen ? 'scaleX(0)' : 'scaleX(1)'};
        transition-delay: 0.3s;
      }

      &:nth-child(2) {
        margin-top: 4px;
        transform-origin: 100% 0;
        transform: ${isOpen ? 'scaleX(0)' : 'scaleX(1)'};
        transition-delay: 0.4s;
      }

      &:nth-child(3) {
        margin-top: 8px;
        transform-origin: 100% 0;
        transform: ${isOpen ? 'scaleX(0)' : 'scaleX(1)'};
        transition-delay: 0.5s;
      }
    }
  `}
`

export const Icon = styled.img`
  width: auto;
`
